import {CONIFER_3, GRANITE_3, WINTER_2} from '@gaia/colors';
import {getWeatherIconCode} from '@gaia/icons/weather/icons';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {formatTemperature, formatWindSpeed, toTitleCase} from '@gaia/utils/formatters';
import {Body2, Caption} from '@map/sidebars/SidebarTypography';
import {Tooltip} from '@mui/material';

import * as icons from './icons';

const precipitationVariant: Record<
    NonNullable<WeatherCondition['precipitation_type']> | 'none',
    {
        color: string;
        icon: OtherWeatherIconCode;
    }
> = {
    none: {color: GRANITE_3, icon: 'rainfallGranite3'},
    any: {color: GRANITE_3, icon: 'mixed'},
    rain: {color: CONIFER_3, icon: 'rainfallSpring3'},
    snow: {color: WINTER_2, icon: 'snowfall'},
};

function getPrecipitationDisplayText(
    precipitation_percent: WeatherCondition['precipitation_percent']
) {
    return `${precipitation_percent}%`;
}

export function PrecipitationChip({
    precipitation_type,
    precipitation_percent,
}: Pick<WeatherCondition, 'precipitation_type' | 'precipitation_percent'>): JSX.Element {
    const precipitationType = precipitation_type ?? 'none';
    const {color, icon} = precipitationVariant[precipitationType];

    const formattedPrecipitationPercent = getPrecipitationDisplayText(precipitation_percent);

    const precipitationTypeText =
        precipitationType === 'any' ? 'Mixed' : toTitleCase(precipitationType);

    const precipitationTooltipLabel = (
        <>
            <Caption component='p'>Precipitation type: {precipitationTypeText}</Caption>
            <Caption component='p'>Probability: {formattedPrecipitationPercent}</Caption>
        </>
    );

    return (
        <WeatherChip
            color={color}
            condition={`Precipitation: ${precipitationTypeText}`}
            icon={icon}
            primaryValue={formattedPrecipitationPercent}
            tooltipLabel={precipitationTooltipLabel}
        />
    );
}

export function WindChip({
    wind_direction,
    wind_avg,
    wind_gust,
}: Pick<WeatherCondition, 'wind_avg' | 'wind_direction' | 'wind_gust'>): JSX.Element {
    const {unitSystem} = useUser();
    const WIND_ICON_CODE: WeatherIconCode = '23';

    const {formattedWindSpeed: formattedWindAvgSpeed, unit} = formatWindSpeed(wind_avg, unitSystem);
    const formattedWindGustSpeed = wind_gust
        ? formatWindSpeed(wind_gust, unitSystem).formattedWindSpeed
        : '';

    const windTooltipLabel = (
        <>
            <Caption component='p'>Wind direction: {wind_direction}</Caption>
            <Caption component='p'>
                Average speed: {formattedWindAvgSpeed} {unit}
            </Caption>
            {Boolean(wind_gust) && (
                <Caption component='p'>
                    Gust speed: {formattedWindGustSpeed} {unit}
                </Caption>
            )}
        </>
    );

    return (
        <WeatherChip
            icon={WIND_ICON_CODE}
            condition='Wind'
            primaryValue={`${wind_direction} ${formattedWindAvgSpeed}`}
            secondaryValue={formattedWindGustSpeed}
            showDivider={true}
            tooltipLabel={windTooltipLabel}
        />
    );
}

export function ForecastChip({
    icon,
    condition,
    temperature_max,
    temperature_min,
}: {
    icon: WeatherIconCode;
    condition: string;
    temperature_max: number;
    temperature_min?: number;
}): JSX.Element {
    const {unitSystem} = useUser();

    const {formattedTemperature: formattedTemperatureMax, unit} = formatTemperature(
        temperature_max,
        unitSystem,
        true
    );
    let formattedTemperatureMin;
    if (temperature_min) {
        const {formattedTemperature} = formatTemperature(temperature_min, unitSystem, true);
        formattedTemperatureMin = formattedTemperature;
    }

    const forecastTooltipLabel = (
        <>
            <Caption component='p'>{condition}</Caption>
            {formattedTemperatureMin ? (
                <>
                    <Caption component='p'>
                        High: {formattedTemperatureMax}
                        {unit}
                    </Caption>
                    <Caption component='p'>
                        Low: {formattedTemperatureMin}
                        {unit}
                    </Caption>
                </>
            ) : (
                <Caption component='p'>
                    Temperature: {formattedTemperatureMax}
                    {unit}
                </Caption>
            )}
        </>
    );

    const iconCode = getWeatherIconCode(icon);

    return (
        <WeatherChip
            icon={iconCode}
            condition={condition}
            primaryValue={`${formattedTemperatureMax}${unit}`}
            secondaryValue={formattedTemperatureMin}
            showDivider={true}
            tooltipLabel={forecastTooltipLabel}
        />
    );
}

export interface WeatherChipProps extends React.HTMLAttributes<HTMLDivElement> {
    color?: string;
    /** The weather icon that should be used */
    icon: WeatherIconCode | OtherWeatherIconCode;
    /** A string describing the condition, to be used as the alt text for `icon` */
    condition: string;
    primaryValue: string;
    secondaryValue?: string;
    showDivider?: boolean;
    tooltipLabel: NonNullable<React.ReactNode>;
}

function isOtherWeatherIconCode(
    icon: WeatherIconCode | OtherWeatherIconCode
): icon is OtherWeatherIconCode {
    const otherCodes: OtherWeatherIconCode[] = [
        'AQI',
        'mixed',
        'offlineCloud',
        'rainfallGranite3',
        'rainfallSpring3',
        'snowfall',
    ];
    return otherCodes.includes(icon as OtherWeatherIconCode);
}

export default function WeatherChip({
    color = GRANITE_3,
    condition,
    icon,
    primaryValue,
    secondaryValue,
    showDivider = false,
    tooltipLabel,
    ...props
}: WeatherChipProps): JSX.Element {
    // In order to align the icon and text properly, the line height should be {ICON HEIGHT}/{FONT SIZE} or 24/14
    const lineHeight = 24 / 14;
    const textStyle: React.CSSProperties = {
        color,
        lineHeight,
        marginRight: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    };
    const secondaryTextStyle = {
        lineHeight,
        color,
    };

    const iconSrc = isOtherWeatherIconCode(icon) ? icons[`${icon}_24`] : icons[`code${icon}_24`];

    // Some values are given as multiple values, such as high/low, max/min, avg/gust
    // These values should display with a / between them.
    return (
        <Tooltip title={tooltipLabel}>
            <div
                style={{
                    ...props.style,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
            >
                <img
                    src={iconSrc}
                    alt={condition}
                    height={24}
                    width={24}
                    style={{marginRight: 2}}
                />
                <Body2 bold style={textStyle}>
                    {primaryValue}
                </Body2>
                {Boolean(secondaryValue) && (
                    <>
                        {showDivider && (
                            <Body2 bold style={textStyle}>
                                /
                            </Body2>
                        )}
                        <Body2 style={secondaryTextStyle}>{secondaryValue}</Body2>
                    </>
                )}
            </div>
        </Tooltip>
    );
}
